// /hoteles — SSR head() with locale-aware SEO for the hotels search listing.
// When search params are present in the URL, the loader fetches real hotel
// results server-side so crawlers (and users) get real content in the initial
// HTML. A 6s timeout inside the server fn prevents SSR hangs on slow upstream.
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { getHost } from "@/lib/host";
import Hotels, { type HotelsInitialData } from "@/pages/Hotels";
import { HotelDetailLoading } from "@/components/HotelDetailLoading";
import { searchHotelsServer } from "@/lib/search-hotels.functions";
import type { HotelSearchParams } from "@/types/iterpec";
import {
  canonicalOriginFromHost,
  localeFromHost,
  type LocaleKey,
} from "@/lib/locale-from-host";
import {
  RouteErrorBoundary,
  RouteNotFoundBoundary,
} from "@/lib/route-boundaries";

const SEO: Record<LocaleKey, { title: string; description: string }> = {
  es: {
    title: "Hoteles en Brasil | Fora da Rota",
    description:
      "Busca y reserva hoteles en Brasil con las mejores tarifas. Cancelación flexible y atención personalizada con Fora da Rota.",
  },
  "pt-BR": {
    title: "Hotéis no Brasil | Fora da Rota",
    description:
      "Busque e reserve hotéis no Brasil com as melhores tarifas. Cancelamento flexível e atendimento personalizado com a Fora da Rota.",
  },
  en: {
    title: "Hotels in Brazil | Fora da Rota",
    description:
      "Search and book hotels in Brazil at the best rates. Flexible cancellation and personalized service with Fora da Rota.",
  },
  "en-EU": {
    title: "Hotels in Brazil | Fora da Rota",
    description:
      "Search and book hotels in Brazil at the best rates. Flexible cancellation and personalized service with Fora da Rota.",
  },
};

// Note: TanStack's default search parser auto-coerces numeric-looking strings
// to numbers, so params like destinationId=1010422 arrive as `number`. Use
// z.coerce.string() to normalize them back to strings without dropping data.
// P2 — antes usávamos `.default("")` para cada campo. Isso fazia o TanStack
// canonicalizar a URL e emitir um 307 (~840ms) toda vez que alguém abria
// /hoteles sem params. Agora tudo é opcional e normalizamos para "" no consumo.
const optStr = fallback(z.coerce.string().optional(), undefined).optional();
const searchSchema = z.object({
  destinationId: optStr,
  destinationName: optStr,
  checkIn: optStr,
  checkOut: optStr,
  numNights: optStr,
  adults: optStr,
  childrenAges: optStr,
});

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function computeCheckout(checkIn: string, checkOut: string, numNights: string): string {
  if (checkOut && ISO_DATE.test(checkOut)) return checkOut;
  if (!checkIn || !ISO_DATE.test(checkIn)) return "";
  const n = parseInt(numNights, 10);
  if (!Number.isFinite(n) || n <= 0) return "";
  const d = new Date(`${checkIn}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().split("T")[0];
}

type HotelsSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/hoteles/")({
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }: { search: HotelsSearch }) => ({
    destinationId: search.destinationId,
    destinationName: search.destinationName,
    checkIn: search.checkIn,
    checkOut: search.checkOut,
    numNights: search.numNights,
    adults: search.adults,
    childrenAges: search.childrenAges,
  }),
  loader: async ({ deps }: { deps: HotelsSearch }) => {
    const host = getHost();
    const base = {
      locale: localeFromHost(host),
      origin: canonicalOriginFromHost(host),
      initialData: null as HotelsInitialData | null,
    };

    const checkIn = deps.checkIn ?? "";
    const checkOut = computeCheckout(checkIn, deps.checkOut ?? "", deps.numNights ?? "");
    const adultsNum = parseInt(deps.adults ?? "", 10);
    if (
      !deps.destinationId ||
      !ISO_DATE.test(checkIn) ||
      !ISO_DATE.test(checkOut) ||
      !Number.isFinite(adultsNum) ||
      adultsNum < 1
    ) {
      return base;
    }

    const childrenAges = deps.childrenAges
      ? deps.childrenAges
          .split(",")
          .map((v) => parseInt(v, 10))
          .filter((n) => Number.isFinite(n) && n >= 0 && n <= 17)
      : [];

    try {
      const result = await searchHotelsServer({
        data: {
          destinationId: deps.destinationId,
          checkIn,
          checkOut,
          adults: Math.min(9, Math.max(1, adultsNum)),
          childrenAges,
        },
      });
      if (!result.ok) return base;

      const params: HotelSearchParams = {
        destinationId: deps.destinationId,
        destinationName: deps.destinationName || undefined,
        checkIn,
        checkOut,
        adults: adultsNum,
        childrenAges,
      };
      return {
        ...base,
        initialData: {
          hotels: result.data.Hotels ?? [],
          token: result.data.Token ?? null,
          params,
        },
      };
    } catch (err) {
      console.error("[/hoteles loader]", err);
      return base;
    }
  },
  head: ({ loaderData }) => {
    const origin = loaderData?.origin ?? "https://www.foradarota.com";
    const locale = loaderData?.locale ?? "es";
    const seo = SEO[locale];
    const url = `${origin}/hoteles`;
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: seo.title },
        { name: "twitter:description", content: seo.description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  errorComponent: RouteErrorBoundary,
  notFoundComponent: () => <RouteNotFoundBoundary />,
  pendingMs: 0,
  pendingMinMs: 0,
  pendingComponent: () => <HotelDetailLoading />,
  component: HotelsRoute,
});

function HotelsRoute() {
  const { initialData } = Route.useLoaderData();
  return <Hotels initialData={initialData ?? undefined} />;
}
